import React from 'react';

type SkeletonVariant = 'lines' | 'card' | 'trip-card' | 'panel' | 'map' | 'hero' | 'story';

type SkeletonProps = {
  count?: number;
  variant?: SkeletonVariant;
  className?: string;
  'aria-label'?: string;
};

const WIDTHS = ['88%', '66%', '78%', '55%', '82%'];

function lineSkeleton(count: number) {
  return Array.from({ length: count }, (_, i) => (
    <span
      className="dd-skeleton-block dd-skeleton-line"
      key={i}
      aria-hidden="true"
      style={{ width: WIDTHS[i % WIDTHS.length] }}
    />
  ));
}

function cardSkeleton() {
  return (
    <>
      <span className="dd-skeleton-block dd-skeleton-card-media" aria-hidden="true" />
      <span className="dd-skeleton-block dd-skeleton-line is-title" aria-hidden="true" />
      <span className="dd-skeleton-block dd-skeleton-line is-short" aria-hidden="true" />
    </>
  );
}

function variantContent(variant: SkeletonVariant, count: number) {
  if (variant === 'lines') return lineSkeleton(count);
  if (variant === 'card') return cardSkeleton();
  if (variant === 'trip-card') {
    return Array.from({ length: count }, (_, index) => (
      <div className="dd-skeleton-trip-card-item" key={index} aria-hidden="true">
        <span className="dd-skeleton-block dd-skeleton-trip-media" />
        <div className="dd-skeleton-trip-copy">
          <span className="dd-skeleton-block dd-skeleton-line is-title" />
          <span className="dd-skeleton-block dd-skeleton-line is-short" />
        </div>
      </div>
    ));
  }
  if (variant === 'panel') {
    return (
      <>
        <span className="dd-skeleton-block dd-skeleton-line is-title" aria-hidden="true" />
        <span className="dd-skeleton-block dd-skeleton-line is-medium" aria-hidden="true" />
        <div className="dd-skeleton-panel-rows" aria-hidden="true">
          {Array.from({ length: Math.max(3, count) }, (_, index) => (
            <span className="dd-skeleton-block dd-skeleton-row" key={index} />
          ))}
        </div>
      </>
    );
  }
  if (variant === 'map') {
    return (
      <>
        <span className="dd-skeleton-map-road road-a" aria-hidden="true" />
        <span className="dd-skeleton-map-road road-b" aria-hidden="true" />
        <span className="dd-skeleton-map-road road-c" aria-hidden="true" />
        <span className="dd-skeleton-block dd-skeleton-map-control control-a" aria-hidden="true" />
        <span className="dd-skeleton-block dd-skeleton-map-control control-b" aria-hidden="true" />
      </>
    );
  }
  if (variant === 'hero') {
    return (
      <>
        <div className="dd-skeleton-hero-copy" aria-hidden="true">
          <span className="dd-skeleton-block dd-skeleton-line is-kicker" />
          <span className="dd-skeleton-block dd-skeleton-line is-heading" />
          <span className="dd-skeleton-block dd-skeleton-line is-medium" />
          <span className="dd-skeleton-block dd-skeleton-button" />
        </div>
        <span className="dd-skeleton-block dd-skeleton-hero-art" aria-hidden="true" />
      </>
    );
  }
  return Array.from({ length: count }, (_, index) => (
        <div className="dd-skeleton-story-row" key={index} aria-hidden="true">
          <span className="dd-skeleton-story-rail" />
          <div className="dd-skeleton-story-card">
            <span className="dd-skeleton-block dd-skeleton-line is-short" />
            <span className="dd-skeleton-block dd-skeleton-story-media" />
            <span className="dd-skeleton-block dd-skeleton-line is-title" />
            <span className="dd-skeleton-block dd-skeleton-line is-medium" />
          </div>
        </div>
      ));
}

export function Skeleton({
  count = 3,
  variant = 'lines',
  className = '',
  'aria-label': ariaLabel = 'Loading',
}: SkeletonProps) {
  return (
    <div
      aria-label={ariaLabel}
      aria-live="polite"
      className={`dd-skeleton dd-skeleton-${variant}${className ? ` ${className}` : ''}`}
      role="status"
    >
      {variantContent(variant, count)}
    </div>
  );
}
